import React from 'react';
import { gradePrices, GRADE_COLORS } from '../../utils/priceColor';

const fmtPrice = (v) => (v === null || v === undefined ? '—' : `£${Number(v).toFixed(2)}`);

/**
 * Library tab — searchable table of master-library entries with cash-equivalent colour coding.
 * Clicking a row opens the barcode detail modal.
 */
export default function LibraryTab({ library, search, setSearch, onSearchSubmit, onRowClick }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
      <div className="p-3 border-b border-zinc-800 flex gap-2">
        <input
          type="text"
          placeholder="Search barcode…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit()}
          className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
          data-testid="admin-search-input"
        />
        <button
          onClick={onSearchSubmit}
          className="px-4 py-2 bg-[#00D26A] text-black font-semibold rounded text-sm"
          data-testid="admin-search-btn"
        >
          Search
        </button>
      </div>
      <div className="px-3 py-2 border-b border-zinc-800 text-[11px] text-zinc-500 flex items-center gap-4" data-testid="library-color-legend">
        <span><span style={{ color: '#FFD700' }}>●</span> Best (cash-equivalent)</span>
        <span><span style={{ color: '#00D26A' }}>●</span> Viable (≥60%)</span>
        <span><span style={{ color: '#E04A3F' }}>●</span> Decline (&lt;60% or £0)</span>
        <span className="ml-auto text-zinc-600">CEX voucher × 0.82</span>
      </div>
      {library.items.length === 0 ? (
        <div className="p-6 text-zinc-500 text-sm text-center" data-testid="library-empty">
          No barcodes in the master library yet. Scan some items and enter prices — they'll appear here live.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Barcode</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-2 py-2">MM</th>
                <th className="text-right px-2 py-2">WBB</th>
                <th className="text-right px-2 py-2">WoB</th>
                <th className="text-right px-2 py-2">CEX¢</th>
                <th className="text-right px-2 py-2">CEX🎟</th>
                <th className="text-right px-2 py-2">Checks</th>
                <th className="text-left px-4 py-2">Last update</th>
              </tr>
            </thead>
            <tbody>
              {library.items.map((it) => {
                const { grades } = gradePrices(it.prices || {});
                const cell = (key) => {
                  const raw = it.prices?.[key];
                  const val = raw && typeof raw === 'object' ? raw.value : raw;
                  const g = GRADE_COLORS[grades[key]] || GRADE_COLORS.grey;
                  return (
                    <td
                      className="px-2 py-2 text-right font-mono"
                      style={{ color: g.text }}
                      data-testid={`library-cell-${key}`}
                    >
                      {fmtPrice(val)}
                    </td>
                  );
                };
                return (
                  <tr
                    key={it.barcode}
                    onClick={() => onRowClick(it.barcode)}
                    className="border-t border-zinc-800 hover:bg-zinc-950 cursor-pointer"
                    data-testid="library-row"
                  >
                    <td className="px-4 py-2 font-mono text-[#FFD700]">{it.barcode}</td>
                    <td className="px-4 py-2 text-xs"><span className="px-2 py-1 rounded bg-zinc-800">{it.status}</span></td>
                    {cell('musicmagpie')}
                    {cell('webuybooks')}
                    {cell('worldofbooks')}
                    {cell('cex_cash')}
                    {cell('cex_voucher')}
                    <td className="px-2 py-2 text-right">{it.total_checks ?? 0}</td>
                    <td className="px-4 py-2 text-zinc-500 text-xs">{it.last_updated?.slice(0, 19).replace('T', ' ') ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
